import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionDef, PermissionSection } from './employeeModel';

/**
 * Τα δικαιώματα ενός ατόμου, ανά ενότητα (ίδια κλειδιά με την παλιά «Δικαιώματα Προσωπικού»).
 * Ελεγχόμενο: δεν αποθηκεύει μόνο του — φεύγουν μαζί με τα στοιχεία του υπαλλήλου, με ένα «Αποθήκευση».
 */
interface EmployeePermissionsProps {
  sections: PermissionSection[];
  isOn: (def: PermissionDef) => boolean;
  onToggle: (def: PermissionDef, next: boolean) => void;
  disabled?: boolean;
}

const EmployeePermissions: React.FC<EmployeePermissionsProps> = ({ sections, isOn, onToggle, disabled }) => {
  const { t } = useTranslation(['employees']);
  const uid = useId();
  return (
    <div className="space-y-5">
      {sections.map(section => (
        <fieldset key={section.id} data-testid={`perm-section-${section.id}`}>
          <legend className="text-xs font-semibold uppercase tracking-wide text-t3 mb-2">
            {t(`employees:permissions.sections.${section.id}`)}
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {section.items.map(def => {
              const on = isOn(def);
              const id = `${uid}-${def.key}`;
              return (
                <label key={def.key} htmlFor={id}
                  className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                    ${on ? 'border-brand-line bg-brand-soft' : 'border-line bg-surface-2 hover:bg-surface-3'}`}>
                  <input id={id} type="checkbox" checked={on} disabled={disabled}
                    onChange={e => onToggle(def, e.target.checked)}
                    aria-labelledby={`${id}-label`} aria-describedby={`${id}-desc`}
                    className="mt-0.5 w-4 h-4 shrink-0 rounded accent-[hsl(var(--brand))]" />
                  <span className="min-w-0">
                    <span id={`${id}-label`} className="block text-sm font-medium text-t1">{t(`employees:perm.${def.key}`)}</span>
                    <span id={`${id}-desc`} className="block text-xs text-t3 mt-0.5">{t(`employees:perm.${def.key}_desc`)}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
};

export default EmployeePermissions;
